import React, { useState, useMemo } from 'react';
import {
  Box,
  Toolbar,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Checkbox,
  Select,
  MenuItem as SelectMenuItem,
  FormControl,
  InputLabel,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  CalendarToday as CalendarIcon,
  ArrowUpward as ArrowUpIcon,
  MoreVert as MoreVertIcon,
  ExpandMore as ExpandMoreIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Assignment as AssignmentIcon,
  BugReport as BugIcon,
  Task as TaskIcon,
  Story as StoryIcon,
  Subtask as SubtaskIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { format, isAfter } from 'date-fns';

const AllTicketsView = ({ tickets, onEditTicket, onDeleteTicket, onCreateTicket }) => {
  // State management
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [labelFilter, setLabelFilter] = useState('all');
  const [moreAnchorEl, setMoreAnchorEl] = useState(null);
  const [priorityFilter, setPriorityFilter] = useState('');
  const [teamFilter, setTeamFilter] = useState('');
  const [reporterFilter, setReporterFilter] = useState('');
  const [closedFilter, setClosedFilter] = useState('');
  const [selectedTickets, setSelectedTickets] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Handle more menu
  const handleMoreClick = (event) => {
    setMoreAnchorEl(event.currentTarget);
  };

  const handleMoreClose = () => {
    setMoreAnchorEl(null);
  };

  // Filter tickets based on current filters
  const filteredTickets = useMemo(() => {
    return tickets.filter(ticket => {
      // Search filter
      if (searchTerm) {
        const searchableText = [
          ticket.id?.toString(),
          ticket.title,
          ticket.summary,
          ticket.description
        ].filter(Boolean).join(' ').toLowerCase();
        
        if (!searchableText.includes(searchTerm.toLowerCase())) {
          return false;
        }
      }

      // Status filter
      if (statusFilter !== 'all' && ticket.status !== statusFilter) {
        return false;
      }

      // Priority filter
      if (priorityFilter && ticket.priority !== priorityFilter) {
        return false;
      }

      // Team filter (based on assignees)
      if (teamFilter && ticket.assignees && !ticket.assignees.some(assignee => 
        teamFilter === 'frontend' && ['S', 'A'].includes(assignee) ||
        teamFilter === 'backend' && ['YT'].includes(assignee) ||
        teamFilter === 'mobile' && ['M'].includes(assignee)
      )) {
        return false;
      }

      // Date range filter
      if (startDate && ticket.createdAt && new Date(ticket.createdAt) < new Date(startDate)) {
        return false;
      }
      if (endDate && ticket.createdAt && new Date(ticket.createdAt) > new Date(endDate)) {
        return false;
      }

      return true;
    });
  }, [tickets, searchTerm, statusFilter, priorityFilter, teamFilter, startDate, endDate]);

  // Handle ticket selection
  const handleSelectTicket = (ticketId) => {
    setSelectedTickets(prev => 
      prev.includes(ticketId) 
        ? prev.filter(id => id !== ticketId)
        : [...prev, ticketId]
    );
  };

  const handleSelectAll = () => {
    if (selectedTickets.length === filteredTickets.length) {
      setSelectedTickets([]);
    } else {
      setSelectedTickets(filteredTickets.map(t => t.id));
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'todo': return 'warning';
      case 'inprogress': return 'primary';
      case 'done': return 'success';
      default: return 'default';
    }
  };

  // Get priority color
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  // Get type icon and color
  const getTypeIcon = (type) => {
    const icons = {
      'task': { icon: '📄', color: '#FFC107' },
      'bug': { icon: '🐛', color: '#F44336' },
      'story': { icon: '📖', color: '#2196F3' },
      'subtask': { icon: '📋', color: '#9C27B0' }
    };
    return icons[type] || { icon: '📄', color: '#FFC107' };
  };

  // Render status chip
  const renderStatusChip = (status) => (
    <Chip
      label={status?.toUpperCase() || 'TODO'}
      size="small"
      color={getStatusColor(status)}
      variant="outlined"
      sx={{ fontWeight: 'bold', textTransform: 'uppercase', fontSize: '10px' }}
    />
  );

  // Render assignee avatar
  const renderAssignee = (assignee) => {
    const initials = assignee ? assignee.charAt(0).toUpperCase() : '?';
    const colors = {
      'S': '#FFD700',
      'YT': '#FFB347', 
      'A': '#FF6347',
      'K': '#00BFFF'
    };
    
    return (
      <Avatar
        sx={{
          width: 24,
          height: 24,
          fontSize: '12px',
          backgroundColor: colors[initials] || '#00BFFF',
          color: 'white',
          border: '1px solid #ccc',
        }}
      >
        {initials}
      </Avatar>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f9f9fb' }}>
      <Toolbar />

      {/* Filter Bar */}
      <Box sx={{ p: 2, bgcolor: '#ffffff', borderBottom: '1px solid #e0e0e0', boxShadow: '0 1px 5px rgba(0,0,0,0.05)' }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Search */}
          <TextField
            placeholder="Search"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 200 }}
          />

          {/* Date Range */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField 
              placeholder="Start date" 
              variant="outlined" 
              size="small" 
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              sx={{ minWidth: 120 }} 
            />
            <Typography variant="body2">→</Typography>
            <TextField 
              placeholder="End date" 
              variant="outlined" 
              size="small" 
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              sx={{ minWidth: 120 }} 
            />
            <IconButton size="small">
              <CalendarIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Status Dropdown */}
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <SelectMenuItem value="all">All</SelectMenuItem>
              <SelectMenuItem value="todo">TODO</SelectMenuItem>
              <SelectMenuItem value="inprogress">In Progress</SelectMenuItem>
              <SelectMenuItem value="done">Done</SelectMenuItem>
            </Select>
          </FormControl>

          {/* Assignee */}
          <Button variant="outlined" size="small" sx={{ minWidth: 140 }}>
            Assignee: All
          </Button>

          {/* Label */}
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Label</InputLabel>
            <Select
              value={labelFilter}
              label="Label"
              onChange={(e) => setLabelFilter(e.target.value)}
            >
              <SelectMenuItem value="all">All</SelectMenuItem>
              <SelectMenuItem value="bug">Bug</SelectMenuItem>
              <SelectMenuItem value="feature">Feature</SelectMenuItem>
              <SelectMenuItem value="ui">UI</SelectMenuItem>
            </Select>
          </FormControl>

          {/* Ticket Type */}
          <Button variant="outlined" size="small" sx={{ minWidth: 140 }}>
            Ticket Type: All
          </Button>

          {/* More Button */}
          <Button
            variant="outlined"
            size="small"
            endIcon={<ExpandMoreIcon />}
            onClick={handleMoreClick}
            sx={{ minWidth: 100 }}
          >
            More
          </Button>
          <Menu anchorEl={moreAnchorEl} open={Boolean(moreAnchorEl)} onClose={handleMoreClose}>
            {/* Priority */}
            <MenuItem>
              <FormControl size="small" fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select value={priorityFilter} label="Priority" onChange={(e) => setPriorityFilter(e.target.value)}>
                  <SelectMenuItem value="">All</SelectMenuItem>
                  <SelectMenuItem value="high">High</SelectMenuItem>
                  <SelectMenuItem value="medium">Medium</SelectMenuItem>
                  <SelectMenuItem value="low">Low</SelectMenuItem>
                </Select>
              </FormControl>
            </MenuItem>

            {/* Team */}
            <MenuItem>
              <FormControl size="small" fullWidth>
                <InputLabel>Team</InputLabel>
                <Select value={teamFilter} label="Team" onChange={(e) => setTeamFilter(e.target.value)}>
                  <SelectMenuItem value="">All</SelectMenuItem>
                  <SelectMenuItem value="frontend">Frontend</SelectMenuItem>
                  <SelectMenuItem value="backend">Backend</SelectMenuItem>
                  <SelectMenuItem value="mobile">Mobile</SelectMenuItem>
                </Select>
              </FormControl>
            </MenuItem>

            {/* Reporter */}
            <MenuItem>
              <FormControl size="small" fullWidth>
                <InputLabel>Reporter</InputLabel>
                <Select value={reporterFilter} label="Reporter" onChange={(e) => setReporterFilter(e.target.value)}>
                  <SelectMenuItem value="">All</SelectMenuItem>
                  <SelectMenuItem value="me">Me</SelectMenuItem>
                  <SelectMenuItem value="others">Others</SelectMenuItem>
                </Select>
              </FormControl>
            </MenuItem>

            {/* Closed Tickets */}
            <MenuItem>
              <FormControl size="small" fullWidth>
                <Select value={closedFilter} onChange={(e) => setClosedFilter(e.target.value)}>
                  <SelectMenuItem value="">Include Open</SelectMenuItem>
                  <SelectMenuItem value="onlyClosed">Only Closed</SelectMenuItem>
                  <SelectMenuItem value="excludeClosed">Exclude Closed</SelectMenuItem>
                </Select>
              </FormControl>
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {/* Ticket Table */}
      <TableContainer component={Paper} sx={{ flex: 1, mt: 0, boxShadow: 'none', border: 'none' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox 
                  size="small" 
                  checked={selectedTickets.length === filteredTickets.length && filteredTickets.length > 0}
                  indeterminate={selectedTickets.length > 0 && selectedTickets.length < filteredTickets.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>Summary</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">A</TableCell>
              <TableCell align="center">R</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTickets.map((ticket) => {
              const typeInfo = getTypeIcon(ticket.type);
              const primaryAssignee = ticket.assignees?.[0] || 'Unassigned';
              const reviewer = ticket.assignees?.[1] || 'K'; // Default reviewer
              
              return (
                <TableRow key={ticket.id} hover selected={selectedTickets.includes(ticket.id)}>
                  <TableCell padding="checkbox">
                    <Checkbox 
                      size="small" 
                      checked={selectedTickets.includes(ticket.id)}
                      onChange={() => handleSelectTicket(ticket.id)}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: '4px',
                          backgroundColor: typeInfo.color,
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                      >
                        <Typography variant="body2" color="black" sx={{ fontSize: '10px' }}>
                          {typeInfo.icon}
                        </Typography>
                      </Box>
                      <ArrowUpIcon sx={{ fontSize: 12, color: typeInfo.color }} />
                      <Typography variant="body2" fontWeight="bold">#{ticket.id}</Typography>
                      <Typography variant="body2" sx={{ ml: 1, color: 'text.primary' }}>
                        {ticket.title}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{renderStatusChip(ticket.status)}</TableCell>
                  <TableCell align="center">{renderAssignee(primaryAssignee)}</TableCell>
                  <TableCell align="center">{renderAssignee(reviewer)}</TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => onEditTicket(ticket)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => onDeleteTicket(ticket.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <IconButton size="small">
                        <MoreVertIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        
        {filteredTickets.length === 0 && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No tickets found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try adjusting your search or filter criteria
            </Typography>
          </Box>
        )}
      </TableContainer>

      {/* Floating Add Button */}
      <Box sx={{ position: 'fixed', bottom: 20, right: 20 }}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={onCreateTicket}
          sx={{
            width: 60,
            height: 60,
            borderRadius: '50%',
            boxShadow: '0 4px 12px rgba(0,123,255,0.2)',
            fontSize: '24px',
          }}
        >
          +
        </Button>
      </Box>
    </Box>
  );
};

export default AllTicketsView;
